import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO, breadcrumb } from "@/lib/seo";
import { useI18n } from "@/i18n";
import { usePageContent } from "@/lib/pageContent";
import { pageImageUrl } from "@/content/pageImages";
import { FAQ } from "@/components/FAQ";
import DevisForm from "@/components/DevisForm";
import { Rich } from "@/components/Rich";
import { SectionTitle, DataTable, LabelledList, EventPageHero } from "@/components/PageBlocks";
import { ArrowRight, Check } from "lucide-react";

const SITE = "https://lentracterestaurant.fr";

const AREA_SERVED = [
  "Ville-d'Avray",
  "Sèvres",
  "Chaville",
  "Saint-Cloud",
  "Marnes-la-Coquette",
  "Boulogne-Billancourt",
  "Issy-les-Moulineaux",
  "Versailles",
  "Paris",
].map((name) => ({ "@type": "City", name }));

/**
 * Shared layout for the themed wedding landing pages
 * (halal / Jewish receptions) — same visual language as /mariage.
 */
export default function ThemedWeddingPage({ pageKey, path, contentByLang, service }) {
  const { lang, localePath, alternateUrls } = useI18n();
  const l = lang === "en" ? "en" : "fr";
  const c = usePageContent(pageKey, l, contentByLang[l]);
  const [imgs, setImgs] = useState({});

  useSEO({
    title: c.meta.title,
    description: c.meta.description,
    keywords: c.meta.keywords,
    lang,
    alternates: alternateUrls(),
    jsonLd: [
      breadcrumb(
        c.breadcrumb.map((b, i) => ({
          name: b.label,
          url: `${SITE}${i === 0 ? "/" : b.to || path}`,
        }))
      ),
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${SITE}${path}#service`,
        name: service.name,
        serviceType: service.serviceType,
        description: c.meta.description,
        provider: { "@id": `${SITE}/#lieu` },
        areaServed: AREA_SERVED,
        offers: {
          "@type": "Offer",
          url: `${SITE}${path}`,
          priceCurrency: "EUR",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            minPrice: 99,
            priceCurrency: "EUR",
            valueAddedTaxIncluded: true,
            unitText: lang === "en" ? "per guest" : "par invité",
          },
        },
      },
    ],
  });

  useEffect(() => {
    api.get("/settings/public").then((r) => setImgs(r.data || {})).catch(() => {});
  }, []);

  const crumbs = c.breadcrumb.map((b) => ({ label: b.label, to: b.to ? localePath(b.to) : undefined }));

  return (
    <div data-testid={`${pageKey}-page`}>
      <EventPageHero
        overline={c.overline}
        h1={c.h1}
        intro={c.intro}
        ctaDevis={c.ctaDevis}
        ctaCall={c.ctaCall}
        image={pageImageUrl(imgs, pageKey, "hero")}
        alt={c.heroAlt}
        crumbs={crumbs}
        phoneSource={`${pageKey}_hero`}
      />

      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1100px] mx-auto">
          <SectionTitle>{c.brief.title}</SectionTitle>
          <div className="mt-10">
            <DataTable caption={c.brief.caption} rows={c.brief.rows} testid={`${pageKey}-brief-table`} />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.engagements.title}</SectionTitle>
          <div className="mt-10">
            <LabelledList items={c.engagements.items} testid={`${pageKey}-engagements`} />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              {pageImageUrl(imgs, pageKey, "menu") && (
                <img
                  src={pageImageUrl(imgs, pageKey, "menu")}
                  alt={c.menu.alt}
                  className="w-full h-full object-cover img-soft"
                />
              )}
            </div>
          </div>
          <div className="md:col-span-7">
            <SectionTitle>{c.menu.title}</SectionTitle>
            <Rich as="p" value={c.menu.intro} className="mt-8 text-[#3D4A5A] leading-relaxed" />
            <dl className="mt-10 space-y-8">
              {c.menu.groups.map((g) => (
                <div key={g.label}>
                  <dt className="text-xs uppercase tracking-[0.22em] text-[#B8956A]">{g.label}</dt>
                  <Rich as="dd" value={g.body} className="mt-3 text-[#3D4A5A] leading-relaxed" />
                </div>
              ))}
            </dl>
            <Rich as="p" value={c.menu.note} className="mt-10 italic text-[#697585]" />
            <Link to={localePath("/menu")} className="btn-ghost mt-10 inline-flex">
              {c.menu.ctaMenu} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.capacityTable.title}</SectionTitle>
          <div className="mt-10">
            <DataTable
              caption={c.capacityTable.caption}
              head={c.capacityTable.head}
              rows={c.capacityTable.rows}
              testid={`${pageKey}-capacity-table`}
            />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.process.title}</SectionTitle>
          <ol className="mt-12 space-y-8" data-testid={`${pageKey}-process`}>
            {c.process.steps.map((s, i) => (
              <li key={i} className="flex items-start gap-6">
                <span className="font-display text-3xl text-[#B8956A] leading-none w-10 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <Rich as="p" value={s} className="text-[#3D4A5A] leading-relaxed pt-1" />
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.zone.title}</SectionTitle>
          <Rich as="p" value={c.zone.intro} className="mt-8 text-[#3D4A5A] leading-relaxed" />
          <ul className="mt-8 space-y-5">
            {c.zone.items.map((it) => (
              <li key={it} className="flex items-start gap-3 text-[#3D4A5A] leading-relaxed">
                <Check size={18} className="text-[#B8956A] mt-1 shrink-0" />
                <Rich value={it} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.faqTitle}</SectionTitle>
          <div className="mt-10">
            <FAQ items={c.faq} testid={`${pageKey}-faq`} schemaId={`ld-faq-${pageKey}`} />
          </div>
        </div>
      </section>

      <section id="devis" className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF] scroll-mt-32">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <p className="overline">{c.overline}</p>
            <SectionTitle>{c.devis.title}</SectionTitle>
            <Rich as="p" value={c.devis.body} className="mt-8 text-[#3D4A5A] leading-relaxed" />
            <p className="mt-6 text-[#697585]">{c.devis.contact}</p>
          </div>
          <div className="md:col-span-7">
            <div className="border border-[#D9D3C5] bg-[#F6F0E7] p-7 md:p-10">
              <DevisForm defaultEventType={c.eventType} pageSource={path} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 lg:px-16 bg-[#F6F0E7] border-t border-[#D9D3C5]">
        <div className="max-w-[1500px] mx-auto">
          <p className="overline">{c.related.title}</p>
          <div className="mt-6 flex flex-wrap gap-x-10 gap-y-4">
            {c.related.links.map((li) => (
              <Link
                key={li.to}
                to={localePath(li.to)}
                className="inline-flex items-center gap-2 text-[#203240] hover:text-[#B8956A] transition-colors"
              >
                {li.label} <ArrowRight size={14} className="text-[#B8956A]" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
